import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  selectWithcookieData,
  selectUser
} from '../../../redux/WithcookieSlice';
import { useTranslation } from '../../../redux/TranslationHelper';
import '../PayLetterPayment/PayLetterPayment.css';

interface PayLetterSuccessProps {
  className?: string;
  style?: React.CSSProperties;
  isEditor?: boolean;
  componentProps?: {
    title?: string;
    successMessage?: string;
    orderButtonText?: string;
    continueButtonText?: string;
    [key: string]: any;
  };
  data?: {
    title?: string;
    successMessage?: string;
    orderButtonText?: string;
    continueButtonText?: string;
    [key: string]: any;
  };
}

interface PayLetterReturnData {
  code?: string;
  message?: string;
  user_id?: string;
  user_name?: string;
  order_no?: string;
  service_name?: string;
  product_name?: string;
  tid?: string;
  cid?: string;
  amount?: number;
  pay_info?: string;
  pgcode?: string;
  transaction_date?: string;
  payhash?: string;
}

/**
 * PayLetter 결제 성공 처리 컴포넌트
 * PayLetter에서 결제 완료 후 리턴되는 페이지
 */
const PayLetterSuccess: React.FC<PayLetterSuccessProps> = ({ 
  className = '', 
  style = {}, 
  isEditor = false,
  componentProps = {},
  data = {}
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch() as any;
  
  const user = useSelector(selectUser);
  const withcookieData = useSelector(selectWithcookieData);
  
  const [isLoading, setIsLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState<PayLetterReturnData | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    // 에디터 모드에서는 더미 데이터 사용
    if (isEditor) {
      setPaymentInfo({
        code: '0',
        message: componentProps.successMessage || data.successMessage || '결제가 성공적으로 완료되었습니다.',
        order_no: 'ORDER_DEMO_12345',
        tid: 'TID_DEMO_67890',
        amount: 42000,
        user_name: '홍길동',
        pgcode: 'creditcard',
        transaction_date: new Date().toISOString()
      });
      setIsLoading(false);
      return;
    }
    
    const processPayLetterSuccess = async () => {
      try {
        // POST로 전달된 데이터는 서버에서 처리하고 
        // 프론트엔드로는 URL 파라미터로 간단한 정보만 전달하는 방식으로 구현
        // (실제 구현 시에는 서버에서 POST 데이터를 받아 처리하고 결과를 전달해야 함)
        
        const searchParams = new URLSearchParams(location.search);
        
        // URL 파라미터에서 정보 추출
        const code = searchParams.get('code');
        const orderId = searchParams.get('order_no');
        const tid = searchParams.get('tid');
        const amount = searchParams.get('amount');
        const message = searchParams.get('message');
        const user_name = searchParams.get('user_name');
        const pgcode = searchParams.get('pgcode');
        const transaction_date = searchParams.get('transaction_date');
        
        // 성공 여부 확인 (code가 0이면 성공)
        if (code === '0' || !code) {
          // 주문 ID에서 checkout ID 추출
          let checkoutId = null;
          if (orderId && orderId.startsWith('ORDER_')) {
            checkoutId = orderId.split('_')[1];
          }
          
          setPaymentInfo({
            code: code || '0',
            message: message || '결제가 성공적으로 완료되었습니다.',
            order_no: orderId || '',
            tid: tid || '',
            amount: amount ? Number(amount) : 0,
            user_name: user_name || '',
            pgcode: pgcode || '',
            transaction_date: transaction_date || ''
          });
          
          setIsLoading(false);
        } else {
          // 결제 실패
          setError(message || t('결제가 실패했습니다.'));
          setIsLoading(false);
        }
      } catch (error: any) {
        console.error('PayLetter 성공 처리 오류:', error);
        setError(error.message || t('결제 처리 중 오류가 발생했습니다.'));
        setIsLoading(false);
      }
    };
    
    processPayLetterSuccess();
  }, [location.search, isEditor]);
  
  const handleGoToOrderHistory = () => {
    navigate('/mypage/orderlist');
  };
  
  const handleGoToHome = () => {
    navigate('/');
  };
  
  if (isLoading) {
    return (
      <div className={`payletter-success-container ${className}`} style={style}>
        <div className="payment-result-card loading">
          <div className="spinner"></div>
          <h2>{t("결제 정보를 처리 중입니다...")}</h2>
          <p>{t("잠시만 기다려주세요.")}</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={`payletter-success-container ${className}`} style={style}>
        <div className="payment-result-card error">
          <div className="error-icon">❌</div>
          <h2>{t("결제 처리 중 오류가 발생했습니다")}</h2>
          <p>{error}</p>
          <div className="payment-action-buttons">
            <button onClick={handleGoToHome} className="go-home-button">
              {t("홈으로 돌아가기")}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`payletter-success-container ${className}`} style={style}>
      <div className="payment-result-card success">
        <div className="success-icon">✓</div>
        <h2>{componentProps.title || data.title || t("결제가 완료되었습니다")}</h2>
        
        {paymentInfo && (
          <div className="payment-info">
            <div className="info-row">
              <span className="label">{t("주문번호")}:</span>
              <span className="value">{paymentInfo.order_no}</span>
            </div>
            {paymentInfo.tid && (
              <div className="info-row">
                <span className="label">{t("거래번호")}:</span>
                <span className="value">{paymentInfo.tid}</span>
              </div>
            )}
            {paymentInfo.amount && paymentInfo.amount > 0 && (
              <div className="info-row">
                <span className="label">{t("결제금액")}:</span>
                <span className="value">
                  {new Intl.NumberFormat('ko-KR', { 
                    style: 'currency', 
                    currency: 'KRW' 
                  }).format(paymentInfo.amount)}
                </span>
              </div>
            )}
            <div className="info-row">
              <span className="label">{t("결제방법")}:</span>
              <span className="value">
                {paymentInfo.pgcode ? `PayLetter (${paymentInfo.pgcode})` : t("PayLetter")}
              </span>
            </div>
            {paymentInfo.user_name && (
              <div className="info-row">
                <span className="label">{t("주문자명")}:</span>
                <span className="value">{paymentInfo.user_name}</span>
              </div>
            )}
            {paymentInfo.transaction_date && (
              <div className="info-row">
                <span className="label">{t("결제일시")}:</span>
                <span className="value">
                  {new Date(paymentInfo.transaction_date).toLocaleString('ko-KR')}
                </span>
              </div>
            )}
            <div className="info-row">
              <span className="label">{t("결제상태")}:</span>
              <span className="value">{t("결제완료")}</span>
            </div>
          </div>
        )}
        
        <div className="payment-action-buttons">
          <button onClick={handleGoToHome} className="go-home-button">
            {t("홈으로 이동하기")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayLetterSuccess; 